/**
 * Replacement-due vocabulary for the bulk issuance grid.
 *
 * The employees endpoint stamps every item row with `replacement_status`
 * (overdue / due_soon / ok / never_issued), the dates behind it and
 * `days_until_replacement`; `?due=` filters on the same statuses and
 * `?due_within_days=` sets how far ahead "due soon" looks. Everything here
 * is a pure function of that payload so it can be unit-tested without
 * rendering the page.
 */

export const DEFAULT_DUE_WITHIN_DAYS = 30;
// The endpoint's own ceiling; kept in the field so the input cannot stage a
// value the request would be refused for.
export const MAX_DUE_WITHIN_DAYS = 3650;

/** Order matters: it is the order the picker lists them, most urgent first. */
export const REPLACEMENT_OPTIONS = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'due_soon', label: 'Due soon' },
  { value: 'never_issued', label: 'Never issued' },
  { value: 'ok', label: 'Up to date' },
];

/** Resolve whatever another screen hands over as a Replacement scope —
 *  status strings, `{ label, value }` options, or a mix — into the option
 *  objects the picker and the page state hold. Anything that is not a known
 *  status is dropped rather than sent, and duplicates collapse. The Due
 *  Replacements report's "Re-issue" sends strings; the page's own picker
 *  sends options; router state is an external input and gets no trust. */
export const toReplacementOptions = (values) => {
  const byValue = new Map(REPLACEMENT_OPTIONS.map((o) => [o.value, o]));
  const seen = new Set();
  const out = [];
  (Array.isArray(values) ? values : []).forEach((v) => {
    const key = typeof v === 'string' ? v : v?.value;
    const option = byValue.get(key);
    if (!option || seen.has(key)) return;
    seen.add(key);
    out.push(option);
  });
  return out;
};

const STYLE = {
  overdue: { color: '#B42318', background: 'rgba(211,0,26,0.10)' },
  due_soon: { color: '#B54708', background: 'rgba(245,158,11,0.16)' },
  never_issued: { color: '#344054', background: 'rgba(52,64,84,0.10)' },
  ok: { color: '#067647', background: 'rgba(6,118,71,0.10)' },
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Whole days from today (local) to an ISO date; null for no date. Used
 *  for per-line rows, whose dates come from the payload without a day
 *  count. `today` is injectable for tests. */
export const daysUntil = (iso, today = new Date()) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return null;
  const target = Date.UTC(y, m - 1, d);
  const base = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target - base) / 86400000);
};

/** What one item row's pill says, and what its hover title spells out.
 *  Returns null for a row that carries no status (the "no uniforms" sentinel
 *  or a payload from a backend that does not send it yet), so the caller can
 *  render a dash rather than a wrong pill. */
export const describeReplacement = (row) => {
  const status = row?.replacement_status;
  if (!status || !STYLE[status]) return null;
  const days = row.days_until_replacement;
  let label;
  if (status === 'overdue') {
    label = days == null ? 'Overdue' : `Overdue ${plural(-days, 'day')}`;
  } else if (status === 'due_soon') {
    label = days === 0 ? 'Due today' : `Due in ${plural(days, 'day')}`;
  } else if (status === 'never_issued') {
    label = 'Never issued';
  } else {
    label = 'Up to date';
  }
  const parts = [];
  if (row.last_issued_date) parts.push(`Last issued ${row.last_issued_date}`);
  if (row.next_replacement_date) parts.push(`replacement due ${row.next_replacement_date}`);
  if (status === 'never_issued') parts.push('No current issuance of this uniform');
  if (status === 'ok' && !row.next_replacement_date) parts.push('No replacement cycle set for this uniform');
  // Several current lines (different sizes or times): the pill speaks for
  // the most urgent, so the rest is said under it and spelled out on hover.
  const lines = Array.isArray(row.lines) ? row.lines : [];
  const detail = (row.due_lines || 0) > 1
    ? `${row.due_lines} lines · ${plural(row.due_quantity || 0, 'unit')}`
    : null;
  const lineText = lines.length > 1
    ? lines.map((l) => `${l.size_code || '—'} × ${l.quantity} issued ${l.issuance_date}${
      l.next_replacement_date ? `, due ${l.next_replacement_date}` : ''}`).join('; ')
    : '';
  const title = [parts.join(' · '), lineText].filter(Boolean).join('. ');
  return { status, label, style: STYLE[status], title, detail };
};

/** One grid row per current issuance line of an item — each with its own
 *  size, quantity, dates and due status — or a single row for an item the
 *  employee was never issued (or from a backend that sends no `lines`).
 *
 *  Keys: `${employee}-${item}-L${lineId}` for a line row, `${employee}-${item}`
 *  otherwise. The submit path merges rows that end up with the same item and
 *  size (see `mergeDuplicateLines`), so three overdue lines of one shirt can
 *  each be re-issued in their own size, or all in one.
 *
 *  `preset_size` is the line's size when the item still offers it, else the
 *  profile size; `due_quantity` is the line's own quantity so a fill re-issues
 *  exactly what that line held. `line_label` is the caption under the name:
 *  "L × 5 · issued 2026-06-01". */
export const lineRowsFor = (emp, it) => {
  const base = {
    company_user: emp.company_user,
    name: emp.name,
    emp_no: emp.emp_no,
    position_name: emp.position_name,
    cost_center_name: emp.cost_center_name,
    gender: emp.gender,
    uniform_item: it.uniform_item,
    uniform_name: it.name,
    sizes: it.sizes || [],
    profile_size: it.profile_size,
    profile_size_code: it.profile_size_code,
    size_confirmation: it.size_confirmation ?? null,
    confirmation_due_date: it.confirmation_due_date ?? null,
    isEmpty: false,
  };
  const lines = Array.isArray(it.lines) ? it.lines : [];
  if (lines.length === 0) {
    return [{
      ...base,
      key: `${emp.company_user}-${it.uniform_item}`,
      preset_size: it.profile_size ?? null,
      replacement_status: it.replacement_status,
      days_until_replacement: it.days_until_replacement,
      last_issued_date: it.last_issued_date,
      next_replacement_date: it.next_replacement_date,
      due_lines: it.due_lines,
      due_quantity: it.due_quantity,
      line_label: null,
    }];
  }
  const offered = new Set((it.sizes || []).map((s) => s.id));
  return lines.map((line, i) => {
    const status = line.replacement_status || it.replacement_status;
    const isDue = status === 'overdue' || status === 'due_soon';
    return {
      ...base,
      key: `${emp.company_user}-${it.uniform_item}-L${line.id ?? i}`,
      line_id: line.id ?? null,
      preset_size: line.size != null && offered.has(line.size) ? line.size : (it.profile_size ?? null),
      replacement_status: status,
      days_until_replacement: daysUntil(line.next_replacement_date),
      last_issued_date: line.issuance_date ?? null,
      next_replacement_date: line.next_replacement_date ?? null,
      due_lines: isDue ? 1 : 0,
      due_quantity: isDue ? (Number(line.quantity) || 0) : 0,
      line_label: `${line.size_code || '—'} × ${line.quantity}${line.issuance_date ? ` · issued ${line.issuance_date}` : ''}`,
    };
  });
};

/** Lines for one employee's payload, with rows that resolved to the same
 *  item and size summed into one — the server rejects a duplicate
 *  (item, size) pair, and two overdue lines re-issued in the same size are
 *  one request for the total. Order of first appearance is kept. */
export const mergeDuplicateLines = (lines) => {
  const byKey = new Map();
  (lines || []).forEach((l) => {
    const k = `${l.uniform_item}-${l.size ?? 'none'}`;
    const cur = byKey.get(k);
    if (cur) cur.quantity += l.quantity;
    else byKey.set(k, { ...l });
  });
  return [...byKey.values()];
};

/** The quantity a re-issue of this row needs: the units across its due
 *  lines, or one for someone who was never issued. Never zero, so a fill
 *  always puts the row into the issuance. */
export const fillQuantityFor = (row) => Math.max(1, Number(row?.due_quantity) || 0);

/** Which of the visible item rows a "Fill" should touch: those whose status
 *  is in the applied Replacement scope — or overdue / due soon when no scope
 *  is applied, never never-issued or up-to-date by default — and that carry
 *  no quantity yet, so typed values are never overwritten. */
export const fillCandidates = (rows, dueScope, qtyMap) => {
  const wanted = new Set(
    (dueScope || []).length > 0 ? dueScope.map((d) => d.value) : ['overdue', 'due_soon'],
  );
  return (rows || []).filter((r) => !r.isEmpty
    && wanted.has(r.replacement_status)
    && !((qtyMap?.[r.key] ?? 0) > 0));
};

/**
 * Where the prefilled Issue Size came from, or null when there is nothing to
 * say — no confirmation window open, or a backend that predates the field.
 *
 * The grid prefills from the employee's profile either way, so a size they
 * confirmed last week and one inherited unchanged for two years render as the
 * same populated select. Only this tells them apart.
 */
export const describeSizeConfirmation = (row) => {
  const state = row?.size_confirmation;
  if (state !== 'confirmed' && state !== 'pending') return null;
  const due = row?.confirmation_due_date;
  if (state === 'confirmed') {
    return {
      tone: 'confirmed',
      text: 'Employee confirmed',
      title: `The employee confirmed this size for the replacement due ${due}.`,
    };
  }
  return {
    tone: 'assumed',
    text: 'Not confirmed',
    title: `Asked for the replacement due ${due}, no reply yet — `
      + 'carrying forward their current size.',
  };
};

/** Counts per status over the item rows in hand (sentinel rows skipped). */
export const tallyReplacement = (rows) => {
  const tally = { overdue: 0, due_soon: 0, never_issued: 0, ok: 0 };
  (rows || []).forEach((r) => {
    if (r?.isEmpty) return;
    const s = r?.replacement_status;
    if (s in tally) tally[s] += 1;
  });
  return tally;
};

/** The short "3 overdue · 2 due soon" summary shown above the grid. Only
 *  the urgent two: the count line is a nudge, not a census. */
export const summariseReplacement = (tally) => [
  tally.overdue > 0 && `${tally.overdue} overdue`,
  tally.due_soon > 0 && `${tally.due_soon} due soon`,
].filter(Boolean).join(' · ');

/** Coerce whatever the days input holds into something the request accepts,
 *  or null when it should be left at the server default. */
export const normaliseDueDays = (value) => {
  const n = Number(value);
  if (value === '' || value == null || !Number.isFinite(n)) return null;
  const whole = Math.trunc(n);
  if (whole < 0) return 0;
  if (whole > MAX_DUE_WITHIN_DAYS) return MAX_DUE_WITHIN_DAYS;
  return whole;
};
